import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ResultRow } from '../../services/l316Api';
import { useL316Query } from './hooks/useL316Query';
import './L316Form.css';

// ---- Helper: format date ----
const fmtDate = (v: string | null): string => (v ? v.substring(0, 10) : '');

// ---- Table columns ----
const columns: ColumnsType<ResultRow> = [
  { title: '通知單號', dataIndex: 'fl160_01', key: 'fl160_01', width: 90, fixed: 'left' },
  {
    title: '通知日期',
    dataIndex: 'fl160_12',
    key: 'fl160_12',
    width: 100,
    render: (v: string | null) => fmtDate(v),
  },
  { title: '受通知人', dataIndex: 'fl160_14', key: 'fl160_14', width: 80 },
  { title: '受通知人姓名', dataIndex: 'notified_person_name', key: 'notified_person_name', width: 100 },
  { title: '受通知部門', dataIndex: 'notified_person_dept', key: 'notified_person_dept', width: 120, ellipsis: true },
  { title: '改善說明', dataIndex: 'fl160_13', key: 'fl160_13', width: 200, ellipsis: true },
  {
    title: '完成',
    dataIndex: 'fl160_15',
    key: 'fl160_15',
    width: 60,
    align: 'center',
    render: (v: boolean) => v ? <Tag color="green">Y</Tag> : <Tag>N</Tag>,
  },
  {
    title: '確認',
    dataIndex: 'fl160_16',
    key: 'fl160_16',
    width: 60,
    align: 'center',
    render: (v: boolean) => v ? <Tag color="blue">Y</Tag> : <Tag>N</Tag>,
  },
  { title: '改善對策', dataIndex: 'fl160_21', key: 'fl160_21', width: 200, ellipsis: true },
  {
    title: '改善完成日',
    dataIndex: 'fl160_22',
    key: 'fl160_22',
    width: 100,
    render: (v: string | null) => fmtDate(v),
  },
  { title: '改善負責人', dataIndex: 'improver_name', key: 'improver_name', width: 100 },
  { title: '確認人員', dataIndex: 'confirmer_name', key: 'confirmer_name', width: 100 },
  {
    title: '確認日期',
    dataIndex: 'fl160_25',
    key: 'fl160_25',
    width: 100,
    render: (v: string | null) => fmtDate(v),
  },
  { title: '追蹤次數', dataIndex: 'fl160_31', key: 'fl160_31', width: 70, align: 'right' },
  {
    title: '追蹤日期',
    dataIndex: 'fl160_32',
    key: 'fl160_32',
    width: 100,
    render: (v: string | null) => fmtDate(v),
  },
  { title: '追蹤人員', dataIndex: 'tracker_name', key: 'tracker_name', width: 100 },
  { title: '追蹤說明', dataIndex: 'fl160_34', key: 'fl160_34', width: 180, ellipsis: true },
  { title: '追蹤結果', dataIndex: 'fl160_35', key: 'fl160_35', width: 70, align: 'right' },
  { title: '稽核員', dataIndex: 'auditor_name', key: 'auditor_name', width: 100 },
  {
    title: '稽核日期',
    dataIndex: 'fl150_11',
    key: 'fl150_11',
    width: 100,
    render: (v: string | null) => fmtDate(v),
  },
  { title: '稽核部門', dataIndex: 'fl150_12', key: 'fl150_12', width: 80 },
  { title: '條文代碼', dataIndex: 'fl151_03', key: 'fl151_03', width: 80 },
  { title: '判定', dataIndex: 'fl151_21', key: 'fl151_21', width: 50 },
];

// ---- Component ----
const L316Form: React.FC = () => {
  const navigate = useNavigate();
  const {
    filter,
    setField,
    onStartNoBlur,
    rows,
    loading,
    queried,
    onQuery,
    onClear,
  } = useL316Query();

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  return (
    <div className="l316-form">
      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>Home</a>
              <span>/</span>
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>ISO</a>
              <span>/</span>
              <span className="current">Audit Improvement</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>稽核改善進度管制</h1>
            <span className="badge">L316</span>
          </div>
        </div>

        <div className="filter-panel">
          <div className="filter-row">
            <span className="filter-label">稽核改善通知單號:</span>
            <span className="filter-bracket">[從]</span>
            <Input
              value={filter.startNo}
              onChange={(e) => setField('startNo', e.target.value)}
              onBlur={onStartNoBlur}
              onKeyDown={onKeyDown}
              placeholder="起始單號"
              maxLength={7}
              style={{ width: 120 }}
              allowClear
            />
            <span className="filter-bracket">[至]</span>
            <Input
              value={filter.lastNo}
              onChange={(e) => setField('lastNo', e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="結束單號"
              maxLength={7}
              style={{ width: 120 }}
              allowClear
            />
            <Button type="primary" onClick={onQuery} loading={loading}>查詢</Button>
            <Button onClick={onClear}>清除</Button>
          </div>
        </div>

        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">共 <strong>{rows.length}</strong> 筆記錄</div>
            </div>
          )}
          {queried ? (
            <Table<ResultRow>
              columns={columns}
              dataSource={rows}
              rowKey={(r, idx) => `${r.fl160_01}-${idx}`}
              pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `共 ${t} 筆` }}
              size="small"
              scroll={{ x: 2400, y: 460 }}
              locale={{ emptyText: '查無資料' }}
              loading={loading}
            />
          ) : (
            <div className="empty-state">設定篩選條件後按「查詢」</div>
          )}
        </div>

        <div className="action-bar">
          <Button danger size="large" onClick={() => navigate('/erp')}>離開</Button>
        </div>
      </div>
    </div>
  );
};

export default L316Form;
